import React, { useState, useEffect, useCallback } from "react";
import { SessionModal, SessionCard } from "@/components";
import { Container, Row, Col, Card, Pagination, Dropdown } from "react-bootstrap";
import CommonLayout from "./commonLayout";
import { SessionCardLoader } from "@/components/loder/index";
import { constant } from "@/utils/constant";
import JotFormComponent from "@/components/jotzform";
import { parseJwt } from "../utils/custom";
import { useLocation, useNavigate } from "react-router-dom";
import { PracticeRunAboutModel, PracticeRunsessionModal } from '../components/modal';

const Session = ({ socket }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAscending, setAscending] = useState(
    !location.state ? true : location.state?.is_ascending
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const [showPracticeRun, setShowPracticeRun] = useState(false);
  const [showPracticeRunSession, setShowPracticeRunSession] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserData = async () => {
    try {
      setUserLoading(true);
      const decodedJwt = parseJwt(localStorage.getItem("token"));
      socket.on("fetch-single-user-data", async (response) => {
        if (response.status === true) {
          // IDOR Protection: Set user credentials for authorization BEFORE setting userData
          // This ensures credentials are on server before any subsequent requests
          if (response.data?.user?.uuid && response.data?.organization?.id) {
            await socket.setUserCredentials(
              response.data.user.uuid,
              response.data.organization.id
            );
          }
          setUserData(response.data);
          setUserLoading(false);
        }
        socket.off("fetch-single-user-data");
      });
      socket.emit("fetch-single-user-data", {
        user_id: decodedJwt.sub,
      });
    } catch (error) {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSessionData = useCallback(
    async (overrideIsAscending) => {
      try {
        const ascending =
          overrideIsAscending !== undefined ? overrideIsAscending : isAscending;

        if (userData?.organization?.id) {
          socket.emit("fetch-session-data-list", {
            id: userData?.organization?.id,
            isAscending: ascending,
            currentPage,
          });
          socket.on("fetch-session-data-list", (response) => {
            if (response.status === true) {
              setData(response.data);
              setIsLoading(false);
            }
            socket.off("fetch-session-data-list");
          });
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, isAscending, socket, userData?.organization?.id]
  );

  useEffect(() => {
    if (!userData?.organization?.id || !socket) return;

    // Join organization room
    socket.emit("join-organization-room", {
      organization_id: userData.organization.id,
    });

    const handleSessionUpdate = async () => {
      setData(null);
      await fetchSessionData();
      // to get the latest user data with organization data
      await fetchUserData();
    };

    socket.on("res-joined-organization-room", (response) => {
      if (response.success) {
        handleSessionUpdate();
      }
      socket.off("res-joined-organization-room");
    });

    socket.on("organization-session-updated", handleSessionUpdate);

    return () => {
      socket.off("organization-session-updated", handleSessionUpdate);
      if (userData?.organization?.id) {
        socket.emit("leave-organization-room", {
          organization_id: userData.organization.id,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.organization?.id, socket]);

  const purchaseModal = () => {
    setShowPurchaseModal(!showPurchaseModal);
  };

  const handleAscending = async (isBool) => {
    setData(null);
    setAscending(isBool);
    fetchSessionData(isBool);
    navigate("/session", {
      state: {
        is_ascending: isBool,
      },
    });
  };

  const handlePageChange = async (newPage) => {
    const totalPage = data && data?.totalPages;
    if (newPage <= totalPage && newPage >= 1) {
      socket.emit("fetch-session-data-list", {
        id: userData?.organization?.id,
        isAscending,
        currentPage: newPage,
      });
      socket.on("fetch-session-data-list", (response) => {
        if (response.status === true) {
          setData(response.data);
          setIsLoading(false);
        }
        socket.off("fetch-session-data-list");
      });
      setCurrentPage(newPage);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const renderPurchaseMessage = () => {
    const message =
      data?.session?.length >= 1
        ? "Schedule or manage sessions below."
        : "Your organization has no upcoming sessions. Schedule a new session below.";

    return (
      <p className="mb-10 text-raven text-15">{isAscending ? message : ""}</p>
    );
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 10;
    const ellipsis = <Pagination.Ellipsis />;
    const keyPrefix = "page";
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;
    if (endPage > data?.totalPages) {
      endPage = data?.totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    if (startPage > 1) {
      pages.push(
        React.cloneElement(ellipsis, { key: `${keyPrefix}_ellipsis_start` })
      );
    }
    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <Pagination.Item
          key={`${keyPrefix}_${page}`}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    if (endPage < data?.totalPages) {
      pages.push(
        React.cloneElement(ellipsis, { key: `${keyPrefix}_ellipsis_end` })
      );
      pages.push(
        <Pagination.Item
          key={`${keyPrefix}_${data?.totalPages}`}
          active={data?.totalPages === currentPage}
          onClick={() => handlePageChange(data?.totalPages)}
        >
          {data?.totalPages}
        </Pagination.Item>
      );
    }

    return pages;
  };

  const handleShow = () => {
    setShow((prevShow) => !prevShow);
  };

	const handleShowPracticeRun = () => {
    setShowPracticeRun(prevShow => !prevShow);
  };

  return (
    <>
      {isLoading || userLoading || data?.session === null ? (
        <SessionCardLoader />
      ) : (
        <CommonLayout
          isAscending={isAscending}
          handleAscending={handleAscending}
          handleShow={handleShow}
          handleShowPracticeRun={handleShowPracticeRun}
          userData={userData}
          socket={socket}
        >
          <Container className="mt-8">
            {[3, 4].includes(userData?.user?.role_id) &&
              renderPurchaseMessage()}
            <Row className='mb-8 gy-7 gx-md-10 gx-2xl-20'>
              {isAscending && (
                <Col lg={4} md={6}>
                  <Card bg="transparent" className="session-card h-full">
                    <Card.Body className="hstack mx-auto">
											<Dropdown className="custom-dropdown">
												<Dropdown.Toggle
													id="dropdown-schedule"
													className="custom-toggle"
												>
													{constant?.scheduleSession}
												</Dropdown.Toggle>

												<Dropdown.Menu className="custom-menu">
													<Dropdown.Item onClick={handleShow}>
														Schedule a Session
													</Dropdown.Item>
													<Dropdown.Item onClick={handleShowPracticeRun}>
														Schedule a Practice Run
													</Dropdown.Item>
												</Dropdown.Menu>
											</Dropdown>
                    </Card.Body>
                  </Card>
                </Col>
              )}
              {data?.session?.map((item, index) => (
                <Col lg={4} md={6} key={index}>
                  <SessionCard data={item} socket={socket} />
                </Col>
              ))}
              {data?.totalPages ? (
                <Col xs={12}>
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {renderPageNumbers()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </Col>
              ) : null}
            </Row>
          </Container>
        </CommonLayout>
      )}
      {show && (
        <SessionModal
          show={show}
          hide={handleShow}
          handleAscending={handleAscending}
          isAscending={isAscending}
          userData={userData}
          socket={socket}
        />
      )}
      {showPurchaseModal && (
        <JotFormComponent show={showPurchaseModal} hide={purchaseModal} />
      )}
			{showPracticeRun && (
				<PracticeRunAboutModel
					show={showPracticeRun}
					hide={handleShowPracticeRun}
					openPracticeRunSessionModel={() => {
						setShowPracticeRunSession(true);
						handleShowPracticeRun();
					}}
				/>
			)}
			{showPracticeRunSession && (
				<PracticeRunsessionModal
					show={showPracticeRunSession}
					hide={()=>{setShowPracticeRunSession(false)}}
					handleAscending={handleAscending}
					isAscending={isAscending}
					userData={userData}
          socket={socket}
				/>
			)}
		</>
	);
};

export default Session;
